import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Text,
  VStack,
  Flex,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "@root/context/global-context";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateToISO } from "@root/utils/date-helper";
import {
  FILE_TYPE,
  MODULE_TYPE,
  PAGINATION_DEFAULTS,
  ROUTE_NAMES,
} from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { FileIcon } from "@root/assets/icons/file-icon";
import { ViewReportButton } from "@root/pages/dashboardpage/style";
import { Paginate } from "@root/components/pagination";
import { set } from "react-datepicker/dist/date_utils";

interface ViewHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  type: number;
}

// Helper function to get module name based on file type
const getModuleName = (type: number, t: (key: string) => string): string => {
  switch (type) {
    case FILE_TYPE.EXTRACTION_REVIEW:
      return t("sidebar.permitReview");
    case FILE_TYPE.EXCEL_AUTO_COMPLETE:
      return t("sidebar.legalReview");
    case FILE_TYPE.FINISHING_MATERIAL_REQUEST:
      return t("sidebar.materialComparison");
    default:
      return "";
  }
};

export const ViewHistoryDrawer = ({
  isOpen,
  onClose,
  type,
}: ViewHistoryDrawerProps) => {
  const { t } = useTranslation("common");
  const { getReports, reports, reportsLoading, reportCount } =
    useGlobalContext();
  const navigate = useNavigate();
  const { downloadPdfOfThread, downloadPdfLoading, setReview } =
    useMainChatContext();
  const [page, setPageNo] = useState(1);

  const moduleName = getModuleName(type, t);

  useEffect(() => {
    console.log(type);
    setPageNo(1);
    if (isOpen) {
      getReports({
        type: type,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: 1,
      });
    }
    console.log(reports);
  }, [isOpen]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageNo(newPage);

      getReports({
        type: type,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: newPage,
      });
    },
    [page]
  );

  // Filter reports for the specific file type
  const filteredReports = useMemo(() => {
    if (!reports) return [];

    // if API response is { data: [...] }
    if (Array.isArray(reports)) return reports;

    if (Array.isArray(reports?.data)) return reports.data;

    return [];
  }, [reports]);

  const handleViewReport = (file: any) => {
    if (file.type === FILE_TYPE.FINISHING_MATERIAL_REQUEST) {
      navigate(`${ROUTE_NAMES.MATERIAL_CAMPARISON}/${file.id}`);
    } else {
      navigate(`${ROUTE_NAMES.PERMIT_REVIEW}/${file.id}`);
    }
  };

  const renderReports = () => {
    if (reportsLoading) {
      return (
        <Flex justify="center" align="center" h="100%">
          <Spinner size="lg" />
        </Flex>
      );
    }

    if (!filteredReports || filteredReports?.length === 0) {
      return (
        <Text textAlign="center" color="gray.500">
          {t("reports.noReportsFound")}
        </Text>
      );
    }

    return (
      <VStack spacing={3} align="stretch">
        {filteredReports?.map((report: any, index: number) => (
          <Flex
            key={report?.id ?? index}
            p={3}
            border="1px solid"
            borderColor="gray.200"
            rounded="md"
            justifyContent="space-between"
            alignItems="center"
            _hover={{ bg: "gray.50" }}
            gap="50px"
          >
            {/* LEFT SIDE */}
            <Flex alignItems="center" gap="10px" flex="1" minW={0}>
              <FileIcon />

              <Flex direction="column" minW={0}>
                <Text fontSize="sm" fontWeight="semibold" isTruncated>
                  {report?.fileName_key}
                </Text>

                <Text fontSize="xs" color="gray.500">
                  {formatDateToISO(report?.createdAt)}
                </Text>
              </Flex>
            </Flex>

            {/* RIGHT SIDE BUTTON */}
            <ViewReportButton
              width="110px"
              minWidth="110px"
              height="35px"
              fontSize="12px"
              flexShrink={0}
              onClick={() => handleViewReport(report)}
            >
              {t("dashboard.viewReport")}
            </ViewReportButton>
          </Flex>
        ))}
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      {/* <DrawerOverlay /> */}
      <DrawerContent bg="white" maxW="450px">
        <DrawerCloseButton />
        <DrawerHeader>
          {moduleName
            ? `${t("translate.previousReports")} (${moduleName})`
            : t("translate.viewHistory")}
        </DrawerHeader>
        <DrawerBody>{renderReports()}</DrawerBody>
        <Flex justifyContent={"center"} mt={4}>
          <Paginate
            page={page}
            count={reportCount}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={handlePageChange}
          />
        </Flex>
      </DrawerContent>
    </Drawer>
  );
};
